import type {
  BookContent,
  GarmentCategory,
  GarmentContent,
  RawBookGarmentsFile,
  RawBookIndexFile,
  RawBookTablesFile,
} from '../types/records'
import type { Repos } from '../state/repos'

const VALID_CATEGORIES: ReadonlyArray<GarmentCategory> = [
  'bodice', 'shirt', 'blouse', 'dress', 'skirt', 'trousers',
  'jacket', 'coat', 'vest', 'sleeve', 'collar', 'other',
]

function asCategory(c: string): GarmentCategory {
  return VALID_CATEGORIES.includes(c as GarmentCategory) ? (c as GarmentCategory) : 'other'
}

export interface ParseResult {
  index: RawBookIndexFile
  tables: RawBookTablesFile
  garments: RawBookGarmentsFile
}

export async function parseImportFiles(files: {
  index: File
  tables: File
  garments: File
}): Promise<ParseResult> {
  const [indexText, tablesText, garmentsText] = await Promise.all([
    files.index.text(),
    files.tables.text(),
    files.garments.text(),
  ])

  let index: RawBookIndexFile
  let tables: RawBookTablesFile
  let garments: RawBookGarmentsFile

  try {
    index = JSON.parse(indexText) as RawBookIndexFile
  } catch (e) {
    throw new Error(`book-index.json non è JSON valido: ${(e as Error).message}`)
  }
  try {
    tables = JSON.parse(tablesText) as RawBookTablesFile
  } catch (e) {
    throw new Error(`book-tables.json non è JSON valido: ${(e as Error).message}`)
  }
  try {
    garments = JSON.parse(garmentsText) as RawBookGarmentsFile
  } catch (e) {
    throw new Error(`book-garments.json non è JSON valido: ${(e as Error).message}`)
  }

  validateIndex(index)
  validateTables(tables)
  validateGarments(garments)

  return { index, tables, garments }
}

function validateIndex(idx: RawBookIndexFile): void {
  if (!idx.metadata?.bookTitle) throw new Error('book-index.json: metadata.bookTitle mancante')
  if (!Array.isArray(idx.garments)) throw new Error('book-index.json: garments non è un array')
}

function validateTables(t: RawBookTablesFile): void {
  if (!t.measurementsAndDivisors?.measurements) throw new Error('book-tables.json: measurementsAndDivisors.measurements mancante')
  if (!t.sizesWomen?.sizes || !t.sizesWomen?.rows) throw new Error('book-tables.json: sizesWomen non valida')
  if (!t.sizesMen?.sizes || !t.sizesMen?.rows) throw new Error('book-tables.json: sizesMen non valida')
}

function validateGarments(g: RawBookGarmentsFile): void {
  if (!Array.isArray(g.garments)) throw new Error('book-garments.json: garments non è un array')
  if (g.garments.length === 0) throw new Error('book-garments.json: nessun capo presente')
}

export function buildBookContent(parsed: ParseResult): BookContent {
  return {
    title: parsed.index.metadata.bookTitle,
    author: parsed.index.metadata.author ?? '',
    publisher: parsed.index.metadata.publisher,
    totalPages: parsed.index.metadata.totalPages,
    importedAt: Date.now(),
    measurementsBase: parsed.tables.measurementsAndDivisors.measurements,
    divisors: parsed.tables.measurementsAndDivisors.divisors ?? [],
    easeAllowance: parsed.tables.easeAllowance?.degrees ?? [],
    sizesWomen: {
      sizes: parsed.tables.sizesWomen.sizes,
      rows: parsed.tables.sizesWomen.rows,
    },
    sizesMen: {
      sizes: parsed.tables.sizesMen.sizes,
      rows: parsed.tables.sizesMen.rows,
    },
  }
}

export function buildGarmentsContent(parsed: ParseResult): GarmentContent[] {
  return parsed.garments.garments.map(g => ({
    ...g,
    category: asCategory(g.category),
  }))
}

export interface ImportProgress {
  phase: 'parsing' | 'saving-book' | 'saving-garments' | 'done'
  garmentsTotal: number
  garmentsSaved: number
}

export async function importBookIntoRepos(
  files: { index: File; tables: File; garments: File },
  repos: Pick<Repos, 'books' | 'garments'>,
  onProgress?: (p: ImportProgress) => void,
): Promise<{ bookId: string; garmentsImported: number }> {
  onProgress?.({ phase: 'parsing', garmentsTotal: 0, garmentsSaved: 0 })
  const parsed = await parseImportFiles(files)

  // Pulizia eventuali libri precedenti — un solo libro alla volta nell'app
  const existingBooks = await repos.books.list()
  for (const b of existingBooks) {
    await repos.books.delete(b.id)
  }
  const existingGarments = await repos.garments.list()
  for (const g of existingGarments) {
    await repos.garments.delete(g.id)
  }

  onProgress?.({ phase: 'saving-book', garmentsTotal: parsed.garments.garments.length, garmentsSaved: 0 })
  const bookContent = buildBookContent(parsed)
  const bookId = await repos.books.create(bookContent)

  const garmentContents = buildGarmentsContent(parsed)
  let saved = 0
  onProgress?.({ phase: 'saving-garments', garmentsTotal: garmentContents.length, garmentsSaved: 0 })
  for (const gc of garmentContents) {
    await repos.garments.create(bookId, gc)
    saved++
    if (saved % 10 === 0 || saved === garmentContents.length) {
      onProgress?.({
        phase: 'saving-garments',
        garmentsTotal: garmentContents.length,
        garmentsSaved: saved,
      })
    }
  }

  onProgress?.({ phase: 'done', garmentsTotal: garmentContents.length, garmentsSaved: saved })
  return { bookId, garmentsImported: saved }
}
